import React from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { CodeIcon } from '@sharefiledev/flow-web';
import { Projects } from '@sharefiledev/icons';
import {
	projectsExtensionSlots,
	projectsOverlays,
	projectsPiletCapabilities,
	ProjectsRoutePath,
	ResourceEntityType,
} from '@sharefiledev/projects-pilet-sdk';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	addBlocks,
	AsyncDashboardBlock,
	AsyncFilesPanel,
	AsyncFilesTileBlock,
	AsyncStaticApp,
} from './Blocks';
import {
	projectsCapabilities,
	projectsContactsCapabilities,
} from './capabilities/capabilities';
import { userIsEntitled } from './entitlementCheck';
import {
	AsyncCreateClientButtonExtension,
	AsyncDeletedFilesTabExtension,
	AsyncFilesTabExtension,
	AsyncFilesTileExtension,
	AsyncHomeTabExtension,
	AsyncShortcutActionButtonExtension,
	AsyncSpecEditorExtension,
} from './Extensions';
import { FeatureFlag } from './featureFlags';
import { CreateProjectOverlay, SuggestedProjectsOverlay } from './overlays';
import {
	AsyncBlockDevelopmentPage,
	AsyncProjectPage,
	AsyncProjectRestorePage,
	AsyncProjectsPage,
	AsyncRecentlyDeletedPage,
} from './Pages';
import { evaluateLocalizedPermissionConfig } from './permissionsConfig';
import { configureHttp, DefaultProjectDataProvider } from './PluginDataProvider';
import { ShortcutExtensionClickActionProps } from './ShortcutActionButton/ShortcutActionButton';
import { getLanguage, ProjectsTitleStrings } from './translate';
import { setupSolutionRedirection } from './utils/solutionRedirect';
import { getVerticalSkin, setVerticalSkinIfNotSet, VerticalSkin } from './verticalSkins';

export function setup(piletApi: PiletApi) {
	const permissionsConfig = evaluateLocalizedPermissionConfig();
	const preset = permissionsConfig.presets.find(
		p => p.rid === 'sharefile:permission_preset-teammember'
	);

	const perms = {
		rid: preset?.rid,
		permissions: preset?.permissions,
	};

	piletApi.setData('urn:projects-pilet-perms', perms);

	// Check if we should register projects routes or redirect to solution.
	const isProjectsSolutionEnabled = getFeatureFlagValue(
		FeatureFlag.EnableProjectAsSolution
	);

	if (!isProjectsSolutionEnabled) {
		// Register normal projects routes
		piletApi.registerPage('/projects/deleted', () => (
			<AsyncRecentlyDeletedPage piletApi={piletApi} />
		));

		piletApi.registerPage('/projects/:projectId/restore', () => (
			<AsyncProjectRestorePage piletApi={piletApi} />
		));

		piletApi.registerPage('/projects/:projectId/:tab?', () => (
			<AsyncProjectPage piletApi={piletApi} />
		));

		piletApi.registerPage('/projects', () => <AsyncProjectsPage piletApi={piletApi} />);
	} else {
		// Solution is installed - handle redirects instead of registering routes
		setupSolutionRedirection();
	}

	if (!userIsEntitled(piletApi)) {
		return;
	}

	piletApi.registerExtension(
		projectsExtensionSlots.tabsExtensionSlots.tabHome,
		({ params }) => <AsyncHomeTabExtension {...params} />
	);

	piletApi.registerExtension(
		projectsExtensionSlots.tabsExtensionSlots.tabFiles,
		({ params }) => <AsyncFilesTabExtension piletApi={piletApi} {...params} />
	);

	piletApi.registerExtension(
		projectsExtensionSlots.tilesExtensionSlots.tileFiles,
		({ params }) => <AsyncFilesTileExtension {...params} />
	);

	addBlocks(piletApi);

	piletApi.registerExtension(
		projectsExtensionSlots.deletedFilesTab,
		({ params }) => <AsyncDeletedFilesTabExtension {...params} />,
		{
			piletSortWeight: 50,
		}
	);

	piletApi.registerExtension(
		'urn:sfextslot:sharefile-appshell:dashboard:shortcuts',
		({ params }) => (
			<AsyncShortcutActionButtonExtension
				piletApi={piletApi}
				{...params}
				ActionButton={
					params.ActionButton as React.ComponentType<ShortcutExtensionClickActionProps>
				}
			/>
		)
	);

	/**
	 * @todo is this really used as a block? Should this be registered in manifest?
	 */
	piletApi.registerExtension(projectsExtensionSlots.staticAppSlot, ({ params }) => (
		<AsyncStaticApp piral={piletApi} params={params} />
	));

	/**
	 * @todo is this really used as a block? Should this be registered in manifest?
	 */
	piletApi.registerExtension(projectsExtensionSlots.dashboardBlock, ({ params }) => (
		<AsyncDashboardBlock piral={piletApi} params={params} />
	));

	/**
	 * @todo is this really used as a block? Should this be registered in manifest?
	 */
	piletApi.registerExtension(projectsExtensionSlots.filesTabBlock, ({ params }) => (
		<AsyncFilesPanel piral={piletApi} params={params} />
	));

	/**
	 * @todo is this really used as a block? Should this be registered in manifest?
	 */
	piletApi.registerExtension(projectsExtensionSlots.filesTileBlock, ({ params }) => (
		<AsyncFilesTileBlock piral={piletApi} params={params} />
	));

	piletApi.registerExtension(
		projectsExtensionSlots.createClientLinkButton,
		({ params }) => <AsyncCreateClientButtonExtension {...params} />
	);

	piletApi.registerDrawer(
		projectsOverlays.createProjectDrawer,
		params => {
			return <CreateProjectOverlay {...params} />;
		},
		{
			drawerProps: {
				width: '400px',
			},
		}
	);

	piletApi.registerDrawer(
		projectsOverlays.suggestedProjectsDrawer,
		params => {
			return <SuggestedProjectsOverlay {...params} />;
		},
		{
			drawerProps: {
				width: '800px',
			},
		}
	);

	piletApi.registerExtension(projectsExtensionSlots.specEditor, ({ params }) => (
		<AsyncSpecEditorExtension {...params} piletApi={piletApi} />
	));

	piletApi.sf.registerLeftNavComponent({
		title: () => {
			try {
				setVerticalSkinIfNotSet();
			} catch {}

			//return t('LeftNav.title');
			// Temporary fix - @citrite/translate does not support using the t() function
			// outside of a <LocalizationLoader /> component.

			return (
				ProjectsTitleStrings.get(getVerticalSkin() ?? VerticalSkin.Default)?.get(
					getLanguage()
				) ?? 'Projects'
			);
		},
		href: isProjectsSolutionEnabled
			? ProjectsRoutePath.ProjectsAsSolution
			: ProjectsRoutePath.LegacyProjects,
		isAvailable: () => true,
		icon: Projects,
		weight: 150,
	});

	piletApi.sf.registerViewEngineResourceURLRewrite({
		resourceType: 'project',
		defaultPath: '/projects/{id}',
		callback: async (rid: string) => {
			const parsed = RID.parse(rid);
			const projectRID = parsed.findEntityType(ResourceEntityType.project);
			return await DefaultProjectDataProvider.getProjectById(projectRID!.entityId);
		},
	});

	piletApi.sf.registerCapability(
		projectsPiletCapabilities.projectsCapabilities,
		projectsCapabilities
	);

	piletApi.sf.registerCapability(
		projectsPiletCapabilities.projectsContactsCapabilities,
		projectsContactsCapabilities
	);

	if (process.env.NODE_ENV === 'development') {
		piletApi.registerPage('/project-block-development', () => (
			<AsyncBlockDevelopmentPage piletApi={piletApi} />
		));

		piletApi.sf.registerLeftNavComponent({
			href: '/project-block-development',
			title: () => 'Project Block Dev',
			icon: () => <CodeIcon />,
			weight: 50,
		});

		if (typeof piletApi.meta.config.apiOrigin === 'string') {
			configureHttp(config => (config.origin = piletApi.meta.config.apiOrigin));
		}
	}
}
